import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const STORAGE_KEY = "gpm_compare";
const MAX_COMPARE = 4;

const CompareContext = createContext({
    ids: [],
    add: () => {},
    remove: () => {},
    clear: () => {},
    isIn: () => false,
    canAdd: false,
});

export function CompareProvider({ children }) {
    const [ids, setIds] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    }, [ids]);

    const add = useCallback((id) => {
        setIds((prev) => {
            if (prev.includes(id)) return prev;
            if (prev.length >= MAX_COMPARE) {
                toast.error(`You can compare up to ${MAX_COMPARE} properties at once. Remove one first.`);
                return prev;
            }
            toast.success("Added to compare");
            return [...prev, id];
        });
    }, []);

    const remove = useCallback((id) => {
        setIds((prev) => prev.filter((x) => x !== id));
    }, []);

    const clear = useCallback(() => setIds([]), []);

    const isIn = useCallback((id) => ids.includes(id), [ids]);

    const value = useMemo(() => ({
        ids,
        add,
        remove,
        clear,
        isIn,
        canAdd: ids.length < MAX_COMPARE,
    }), [ids, add, remove, clear, isIn]);

    return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}

export function useCompare() {
    return useContext(CompareContext);
}

export { MAX_COMPARE };
